import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import './theme.css';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { apiFetch } from './utils/api';
import {
  MAX_LENGTHS,
  validateText,
  escapeForDisplay,
} from './utils/inputValidation';
import { DEFAULT_PRODUCT_CATEGORY, buildAnalyzeRequestBody } from './utils/productCategory';
// The signup grant, for the pending-activation banner. One number, mirrored
// from the backend and tethered by test_plan_credits_match_frontend.py.
import { TRIAL_SIGNUP_CREDITS } from './constants/plans';
import TopNav from './components/TopNav';
import Sidebar from './components/Sidebar';
import MobileSidebar from './components/MobileSidebar';
import Footer from './components/Footer';
import CreditExhaustedModal from './components/CreditExhaustedModal';
import LowCreditBanner from './components/LowCreditBanner';
// DASH comes from the panel so the whole app has ONE "we do not know" glyph:
// CreditExhaustedModal renders whatever `balance` it is handed, and it must
// render the same dash the panel above it is showing (O3).
import OrgCreditPanel, { DASH } from './components/OrgCreditPanel';
import { firstLine } from './components/OriginalInputCollapse';
import DashboardSummaryCards from './components/DashboardSummaryCards';
import ExampleClaimsSection from './components/ExampleClaimsSection';
import AdminTestRunner from './AdminTestRunner';
import DataExplorer from './DataExplorer';
import LoginPage from './LoginPage';
import ProfilePage from './ProfilePage';
import UserManagement from './UserManagement';
import SiteAdminUserManager from './SiteAdminUserManager';
import ManualPaymentManager from './ManualPaymentManager';
import GoldDatasetManager from './GoldDatasetManager';
import RoleManagement from './RoleManagement';
import AuditLog from './AuditLog';
import UserReportPage from './UserReportPage';
import AdminProfileManager from './AdminProfileManager';
import AdminSessionManager from './AdminSessionManager';
import EmailServiceManager from './EmailServiceManager';
import PipelineHealthDashboard from './components/PipelineHealthDashboard';
import LandingPage from './components/landing/LandingPage';
import PricingPage from './components/landing/PricingPage';
import SettingsPage from './pages/SettingsPage';
import RecordsPage from './pages/RecordsPage';
import AboutPage from './pages/AboutPage';
import ManageMembershipPage from './pages/ManageMembershipPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CancelDeletionPage from './pages/CancelDeletionPage';
import ReportDetailPage from './pages/ReportDetailPage';
import SignupPendingPage from './pages/SignupPendingPage';
import VerifiedPage from './pages/VerifiedPage';
import MarketingAIPage from './MarketingAIPage';
import PromotionManager from './PromotionManager';
import RefundManager from './RefundManager';

// Layout wrapper for authenticated pages with sidebar
function AppLayout({ children, theme, setTheme }) {
  const { user } = useAuth();
  const location = useLocation();

  // No layout wrapper for landing page, pricing page (they have their own structure)
  if (location.pathname === '/' || location.pathname === '/pricing') {
    return <>{children}</>;
  }

  // Standalone auth/onboarding pages: no sidebar, no admin chrome — even when
  // a stale session exists. Critical so a logged-in user clicking a password
  // reset link does NOT see the authenticated app before redirect.
  const standalonePaths = [
    '/login',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/signup/pending',
    '/verify-email',
    '/verified',
    '/account/cancel-deletion',
  ];
  if (standalonePaths.some(p => location.pathname.startsWith(p))) {
    return <>{children}</>;
  }

  // Public report share page renders its own PublicHeader for guests and is
  // safe under the authenticated layout for owners — keep default behavior.

  // About page: public — no sidebar but with TopNav
  if (location.pathname === '/about') {
    return (
      <div className="flex flex-col min-h-screen" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
        <TopNav theme={theme} setTheme={setTheme} />
        <div className="flex-1 pt-14">
          {children}
        </div>
        <Footer />
      </div>
    );
  }
  
  // Show sidebar layout for authenticated pages
  if (user) {
    return (
      <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
        {/* TopNav — hamburger lives inline in the header on mobile (left of logo) */}
        <TopNav theme={theme} setTheme={setTheme} leftSlot={<MobileSidebar />} />

        {/* Desktop Sidebar - hidden on mobile */}
        <div className="hidden md:block" style={{ paddingTop: 0 }}>
          <Sidebar />
        </div>
        
        {/* Mobile hamburger now renders inline inside TopNav (see leftSlot) */}
        
        {/* Main Content Area */}
        <main className="flex flex-col flex-1 overflow-auto" style={{ paddingTop: 56, backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
          <div className="flex-1">
            {children}
          </div>
          <Footer />
        </main>
      </div>
    );
  }
  
  // Fallback for unauthenticated pages
  return (
    <>
      {children}
      <Footer />
    </>
  );
}

const PRESET_EXAMPLES = {
  drug: {
    titleKey: 'exampleClaims.drugModalTitle',
    buttonKey: 'exampleClaims.drugButton',
    tooltipKey: 'exampleClaims.drugTooltip',
    color: '#f44336',
    claims: [
      'Treats acne and reduces inflammation',
      'Cures eczema and heals damaged skin',
      'Prevents skin infections and eliminates bacteria',
      'Relieves psoriasis symptoms and reduces flare-ups',
      'Heals wounds and repairs tissue damage',
      'Treats rosacea and chronic facial redness',
      'Clinically proven to eliminate wrinkles permanently',
      'Repairs DNA damage and restores cellular function',
      'Anti-inflammatory treatment for dermatitis',
      'Stimulates collagen production to reverse skin aging',
      'hydroquinone cream to bleach skin pigment'
    ]
  },
  otc: {
    titleKey: 'exampleClaims.otcModalTitle',
    buttonKey: 'exampleClaims.otcButton',
    tooltipKey: 'exampleClaims.otcTooltip',
    color: '#ff9800',
    claims: [
      'Helps prevent sunburn with SPF 50 broad spectrum protection',
      'Relieves minor skin irritation and itching',
      'Antibacterial hand wash kills 99.9% of germs',
      'Dandruff shampoo controls flaking and scalp itch',
      'Acne treatment with 2% salicylic acid',
      'First aid antiseptic for minor cuts and scrapes',
      'Anti-itch cream for insect bites and minor rashes',
      'Fluoride toothpaste prevents cavities',
      'Pain-relieving topical analgesic for muscle aches',
      'Antiperspirant reduces underarm perspiration for 48 hours',
    ]
  },
  cosmetic: {
    titleKey: 'exampleClaims.cosmeticModalTitle',
    buttonKey: 'exampleClaims.cosmeticButton',
    tooltipKey: 'exampleClaims.cosmeticTooltip',
    color: '#4caf50',
    claims: [
      'Moisturizes and hydrates skin for a healthy glow',
      'Gently cleanses and removes makeup without irritation',
      'Improves the appearance of fine lines and wrinkles',
      'Leaves skin feeling soft, smooth, and refreshed',
      'Adds volume and shine to dull, lifeless hair',
      'Provides long-lasting hydration for dry skin',
      'Enhances your natural radiance with light-reflecting pigments',
      'Gently exfoliates to reveal brighter-looking skin',
      'Nourishes and conditions with vitamins and botanical extracts',
      'Mattifies oily skin for a fresh, shine-free look all day',
    ]
  }
};

function PipelineStages({ result }) {
  const [expandedStage, setExpandedStage] = useState(null);
  const meta = result.agent_metadata || {};

  const toggleStage = (stage) => {
    setExpandedStage(expandedStage === stage ? null : stage);
  };

  // Stage 1: Claim Extraction
  const extraction = meta.extraction?.result || {};
  const extractionStatus = result.extracted_claims?.length > 0 ? 'success' : 'warning';

  // Stage 2: Vector Retrieval
  const vector = meta.vector_retrieval || {};
  const drugSim = vector.drug_similarity || {};
  const cosmeticSim = vector.cosmetic_similarity || {};
  const otcSim = vector.otc_similarity || {};
  const delta = meta.delta_drug_cosmetic || 0;
  const vectorStatus = vector.drug_similarity ? 'success' : 'skipped';

  // Stage 3: Rule Engine
  const rules = meta.rules?.result || {};
  const ruleCount = result.rule_hits?.length || 0;
  const rulesStatus = ruleCount > 0 ? (rules.has_veto ? 'veto' : 'success') : 'success';

  // Stage 4: Ensemble Classifier
  const ensemble = meta.ensemble?.result || {};
  const probs = meta.ensemble_probabilities || {};
  const vetoActive = meta.veto_active || false;
  const ensembleStatus = vetoActive ? 'veto' : 'success';

  // Stage 5: Reasoning
  const citations = meta.citations || [];
  const recommendations = meta.recommendations || [];
  const reasoningStatus = citations.length > 0 || recommendations.length > 0 ? 'success' : 'warning';

  const stages = [
    {
      key: 'extraction',
      icon: '🔍',
      title: 'Claim Extraction',
      status: extractionStatus,
      summary: `${result.extracted_claims?.length || 0} claims extracted`,
      content: (
        <div className="stage-detail">
          <div className="stage-stat-row">
            <span className="stage-stat-label">Method:</span>
            <span>{extraction.method || 'NLP + Heuristic'}</span>
          </div>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Claims Found:</span>
            <span className="stage-stat-value">{result.extracted_claims?.length || 0}</span>
          </div>
          {result.extracted_claims?.map((claim, i) => (
            <div key={i} className="stage-claim-item">
              <span className="stage-claim-num">#{i + 1}</span> {claim}
            </div>
          ))}
        </div>
      )
    },
    {
      key: 'vector',
      icon: '🧲',
      title: 'Vector Retrieval',
      status: vectorStatus,
      summary: vectorStatus === 'skipped'
        ? 'No embeddings available'
        : `Δ = ${delta >= 0 ? '+' : ''}${delta.toFixed(3)} | Drug: ${(drugSim.max_score || 0).toFixed(3)} | Cosmetic: ${(cosmeticSim.max_score || 0).toFixed(3)}`,
      content: (
        <div className="stage-detail">
          <div className="stage-stat-row">
            <span className="stage-stat-label">Delta (Drug − Cosmetic):</span>
            <span className={`stage-stat-value ${delta > 0.3 ? 'text-drug' : delta < -0.3 ? 'text-cosmetic' : 'text-neutral'}`}>
              {delta >= 0 ? '+' : ''}{delta.toFixed(3)}
            </span>
          </div>
          <h4 className="stage-subheading">Drug Precedent Index</h4>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Max Similarity:</span>
            <span className="stage-stat-value">{(drugSim.max_score || 0).toFixed(3)}</span>
          </div>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Matches:</span>
            <span>{drugSim.match_count || 0}</span>
          </div>
          {drugSim.matches?.slice(0, 3).map((m, i) => (
            <div key={i} className="stage-match-item drug">
              <span className="match-score">{(m.similarity || m.score || 0).toFixed(3)}</span>
              <span className="match-text">{m.claim_text || m.precedent || 'N/A'}</span>
            </div>
          ))}

          <h4 className="stage-subheading">Cosmetic Safe Index</h4>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Max Similarity:</span>
            <span className="stage-stat-value">{(cosmeticSim.max_score || 0).toFixed(3)}</span>
          </div>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Matches:</span>
            <span>{cosmeticSim.match_count || 0}</span>
          </div>
          {cosmeticSim.matches?.slice(0, 3).map((m, i) => (
            <div key={i} className="stage-match-item cosmetic">
              <span className="match-score">{(m.similarity || m.score || 0).toFixed(3)}</span>
              <span className="match-text">{m.claim_text || m.description || 'N/A'}</span>
            </div>
          ))}

          <h4 className="stage-subheading">OTC Monograph Index</h4>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Max Similarity:</span>
            <span className="stage-stat-value">{(otcSim.max_score || 0).toFixed(3)}</span>
          </div>
          {otcSim.matches?.slice(0, 2).map((m, i) => (
            <div key={i} className="stage-match-item otc">
              <span className="match-score">{(m.similarity || m.score || 0).toFixed(3)}</span>
              <span className="match-text">{m.claim_text || 'N/A'}</span>
            </div>
          ))}
        </div>
      )
    },
    {
      key: 'rules',
      icon: '📏',
      title: 'Rule Engine',
      status: rulesStatus,
      summary: rulesStatus === 'veto'
        ? `VETO ACTIVE — ${ruleCount} rule(s) triggered`
        : `${ruleCount} rule(s) triggered | Classification: ${rules.classification || '—'}`,
      content: (
        <div className="stage-detail">
          <div className="stage-stat-row">
            <span className="stage-stat-label">Classification:</span>
            <span className="stage-stat-value">{rules.classification || '—'}</span>
          </div>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Risk Level:</span>
            <span className="stage-stat-value">{rules.risk_level || '—'}</span>
          </div>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Confidence:</span>
            <span className="stage-stat-value">{rules.confidence ? (rules.confidence * 100).toFixed(0) + '%' : '—'}</span>
          </div>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Veto Power:</span>
            <span className={`stage-stat-value ${rules.has_veto ? 'text-drug' : ''}`}>
              {rules.has_veto ? '⚡ ACTIVE' : 'Inactive'}
            </span>
          </div>
          {rules.veto_reason && (
            <div className="stage-veto-reason">{rules.veto_reason}</div>
          )}
          {rules.vector_signal && (
            <div className="stage-stat-row">
              <span className="stage-stat-label">Vector Signal:</span>
              <span>{rules.vector_signal}</span>
            </div>
          )}
          {rules.confidence_breakdown && (
            <>
              <h4 className="stage-subheading">Confidence Breakdown</h4>
              <div className="stage-stat-row">
                <span className="stage-stat-label">Rule Confidence:</span>
                <span>{(rules.confidence_breakdown.rule_confidence * 100).toFixed(0)}%</span>
              </div>
              <div className="stage-stat-row">
                <span className="stage-stat-label">Vector Confidence:</span>
                <span>{(rules.confidence_breakdown.vector_confidence * 100).toFixed(0)}%</span>
              </div>
              <div className="stage-stat-row">
                <span className="stage-stat-label">Combined:</span>
                <span className="stage-stat-value">{(rules.confidence_breakdown.combined_confidence * 100).toFixed(0)}%</span>
              </div>
            </>
          )}
        </div>
      )
    },
    {
      key: 'ensemble',
      icon: '🎯',
      title: 'Ensemble Classifier',
      status: ensembleStatus,
      summary: vetoActive
        ? `VETO OVERRIDE → ${result.classification} at ${(result.confidence * 100).toFixed(0)}%`
        : `${result.classification} at ${(result.confidence * 100).toFixed(0)}% confidence`,
      content: (
        <div className="stage-detail">
          <div className="stage-stat-row">
            <span className="stage-stat-label">Final Classification:</span>
            <span className="stage-stat-value">{ensemble.classification || result.classification}</span>
          </div>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Confidence:</span>
            <span className="stage-stat-value">{(result.confidence * 100).toFixed(0)}%</span>
          </div>
          <div className="stage-stat-row">
            <span className="stage-stat-label">Veto Active:</span>
            <span className={vetoActive ? 'text-drug' : ''}>{vetoActive ? 'Yes ⚡' : 'No'}</span>
          </div>
          {Object.keys(probs).length > 0 && (
            <>
              <h4 className="stage-subheading">Probability Distribution</h4>
              {Object.entries(probs).map(([label, prob]) => (
                <div key={label} className="stage-prob-row">
                  <span className="stage-prob-label">{label}</span>
                  <div className="stage-prob-bar-bg">
                    <div
                      className={`stage-prob-bar ${label.toLowerCase().includes('drug') && !label.toLowerCase().includes('otc') ? 'bar-drug' : label.toLowerCase().includes('otc') ? 'bar-otc' : 'bar-cosmetic'}`}
                      style={{ width: `${(prob * 100).toFixed(0)}%` }}
                    />
                  </div>
                  <span className="stage-prob-value">{(prob * 100).toFixed(1)}%</span>
                </div>
              ))}
            </>
          )}
          {ensemble.method && (
            <div className="stage-stat-row">
              <span className="stage-stat-label">Method:</span>
              <span>{ensemble.method}</span>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'reasoning',
      icon: '💡',
      title: 'Reasoning & Citations',
      status: reasoningStatus,
      summary: `${citations.length} citation(s) | ${recommendations.length} recommendation(s)`,
      content: (
        <div className="stage-detail">
          {meta.regulatory_basis && (
            <>
              <h4 className="stage-subheading">Regulatory Basis</h4>
              <div className="stage-text-block">{meta.regulatory_basis}</div>
            </>
          )}
          {citations.length > 0 && (
            <>
              <h4 className="stage-subheading">Citations</h4>
              {citations.map((c, i) => (
                <div key={i} className="stage-citation-item">
                  <div className="citation-source">{c.source} § {c.section}</div>
                  <div className="citation-desc">{c.description}</div>
                  {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="citation-link">View source →</a>}
                </div>
              ))}
            </>
          )}
          {meta.confidence_explanation && (
            <>
              <h4 className="stage-subheading">Confidence Explanation</h4>
              <div className="stage-text-block">{meta.confidence_explanation}</div>
            </>
          )}
          {meta.risk_assessment && (
            <>
              <h4 className="stage-subheading">Risk Assessment</h4>
              <div className="stage-text-block">{meta.risk_assessment}</div>
            </>
          )}
          {recommendations.length > 0 && (
            <>
              <h4 className="stage-subheading">Recommendations</h4>
              <ul className="stage-recommendations">
                {recommendations.map((rec, i) => (
                  <li key={i}>{rec}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="pipeline-stages">
      <h3>Agent Pipeline Details</h3>
      <div className="pipeline-flow">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.key}>
            <div className={`pipeline-stage ${stage.status} ${expandedStage === stage.key ? 'expanded' : ''}`}>
              <button className="stage-header" onClick={() => toggleStage(stage.key)}>
                <span className="stage-icon">{stage.icon}</span>
                <div className="stage-info">
                  <span className="stage-title">
                    {index + 1}. {stage.title}
                    <span className={`stage-status-dot ${stage.status}`} />
                  </span>
                  <span className="stage-summary">{stage.summary}</span>
                </div>
                <span className={`stage-chevron ${expandedStage === stage.key ? 'open' : ''}`}>▼</span>
              </button>
              {expandedStage === stage.key && (
                <div className="stage-body">{stage.content}</div>
              )}
            </div>
            {index < stages.length - 1 && <div className="pipeline-connector">↓</div>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function AnalysisPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const navigate = useNavigate();
  const location = useLocation();
  const [text, setText] = useState('');
  const [source, setSource] = useState('Product Label');
  const [productCategory, setProductCategory] = useState(DEFAULT_PRODUCT_CATEGORY);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState(null);
  const [membershipTier, setMembershipTier] = useState(null);
  // T11 — the ORG pool as the shared panel needs it. `/profile/membership`
  // already returns `org_credits` (the allowance and this period's usage) and
  // this page fetched it and threw it away, then printed `balance` from a
  // second call with no allowance, no usage and no renewal date beside it.
  const [orgCredits, setOrgCredits] = useState(null);
  // The membership term, kept ONLY so the panel can print 다음 제공일 (was
  // 갱신일): credit periods run on the purchase anniversary, not the calendar
  // 1st (Q2). Null unless the membership is ACTIVE — a pending row renews
  // nothing. `end_date` travels with `start_date` because the anniversary walk
  // has to be clamped to the term: on or past `end_date` the grant sweep
  // refuses (plan_credit_service.grant_due_for_membership; T3.6 stopped the
  // 13th grant ON the end date), so the final month of a 12-month plan must
  // show a dash, not a grant date nobody will honour.
  const [planStartDate, setPlanStartDate] = useState(null);
  const [planEndDate, setPlanEndDate] = useState(null);
  // Distinguishes "not loaded yet" from "loaded and we still do not know".
  // Without it the panel would accuse a 200ms-slow network of being an error.
  const [creditsLoaded, setCreditsLoaded] = useState(false);
  const [pendingMembership, setPendingMembership] = useState(null);
  const [dismissPending, setDismissPending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [welcomeOrg, setWelcomeOrg] = useState(location.state?.welcomeOrg || null);
  const [exampleModal, setExampleModal] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  // Incomplete / low-confidence claim → the server made NO report and charged
  // NOTHING. We surface a calm "needs review" balloon instead of navigating.
  // See .claude/TODO/Incomplete-claim-fix-7-12.md. LEGACY-ONLY: never set for
  // a CU-metered response (analysis.cu_charged != null) — see T5.3 gating
  // note at handleConfirmAnalyze. A CU request was analysed and charged, so
  // there is always a result to show; the old "couldn't judge this" framing
  // does not apply.
  // Real balance/shortfall numbers for the credit-exhausted modal, from a
  // genuine 402 body ({error, required_credits, credits_remaining,
  // shortfall}). null falls back to the modal's balance-only copy.
  //
  // The `estimate` / `estimateLoading` / `overCapNotice` state that used to
  // live here is gone with the /estimates pre-flight
  // (CU-Based-pricing-3000-char-8-13.md D5): with a flat 1-credit price there
  // is no quote to fetch, and with a hard char cap enforced in the box there
  // is no server-side over-cap verdict to render.
  const [creditShortfall, setCreditShortfall] = useState(null);

  // The analyze textbox: 300 characters, 1 credit, whatever fits
  // (CU-Based-pricing-3000-char-8-13.md). This profile is mirrored from the
  // backend `analyze_text` guard profile and parity is asserted by
  // backend/tests/security/test_frontend_cap_parity.py — the number below and
  // the number the server enforces can never drift apart silently.
  const ANALYZE_PROFILE = 'analyze_text';
  const ANALYZE_CHAR_LIMIT = MAX_LENGTHS[ANALYZE_PROFILE];
  // DoS backstop, NOT the business rule. The textarea deliberately does not
  // stop typing at the limit (see the maxLength note on the element), so
  // something has to stop a 10 MB paste from wedging the tab. 10x the limit is
  // far past any legitimate input while leaving the over-limit state — the one
  // this whole flow exists to explain — comfortably reachable.
  const ANALYZE_HARD_MAX = ANALYZE_CHAR_LIMIT * 10;
  const claimResult = validateText(text, ANALYZE_PROFILE);
  // SOFT vs HARD invalid. Over-length is the expected, explainable state: the
  // counter turns red, a message appears under it, and the Analyze button
  // STAYS CLICKABLE so pressing it can say why (a greyed-out button with no
  // explanation is the failure this replaced). Everything else validateText
  // can return — NUL bytes, a non-string — is unexplainable to the user and
  // still hard-disables submission.
  const overCharLimit = !claimResult.ok && claimResult.error_code === 'INPUT_TOO_LONG';
  const hardInvalid = !claimResult.ok && !overCharLimit;
  const charCount = text.length;

  const loadBalance = useCallback(async () => {
    try {
      const [balRes, memRes] = await Promise.all([
        apiFetch('/api/v1/credits/balance'),
        apiFetch('/api/v1/profile/membership'),
      ]);
      if (balRes.ok) {
        const data = await balRes.json();
        // ⚠ number|null, NEVER 0 (O3). `/credits/balance` is being changed to
        // return null when it cannot read the org row — "we do not know" —
        // instead of the 0 that used to print as an empty pool. A missing key
        // would arrive as `undefined` and quietly defeat every `=== null`
        // check below (the panel's unavailableReason, the analyse guard, the
        // summary card), so it is normalised here, once, at the boundary.
        setBalance(typeof data.balance === 'number' && Number.isFinite(data.balance)
          ? data.balance
          : null);
        setMembershipTier(data.membership_tier || null);
      }
      if (memRes.ok) {
        const memData = await memRes.json();
        if (memData?.membership?.status === 'pending') {
          setPendingMembership(memData.membership);
        }
        setOrgCredits(memData?.org_credits || null);
        const active = memData?.membership?.status === 'active';
        setPlanStartDate(active ? (memData.membership.start_date || null) : null);
        setPlanEndDate(active ? (memData.membership.end_date || null) : null);
      }
    } catch { /* ignore */ }
    finally { setCreditsLoaded(true); }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/reports?page=1&per_page=3');
      if (res.ok) {
        const data = await res.json();
        setRecentReports(data.reports || data.items || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadBalance(); loadRecent(); }, [loadBalance, loadRecent]);

  const handleAnalyzeClick = () => {
    if (!text.trim()) { setError('Please enter some text to analyze'); return; }
    setError(null);
    setCreditShortfall(null);
    const isAdmin = user?.role === 'admin';

    // OVER THE LIMIT -> explain and stop. This runs BEFORE anything else and
    // issues NO network request: the server would reject the same body with a
    // 422, so a round-trip could only make the user wait to be told what the
    // counter above the button has been saying since they crossed the limit. The
    // inline message under the counter stays visible at the same time — two
    // surfaces, one truth.
    if (overCharLimit) {
      setError(t('analyze.charLimit.blocked', {
        count: charCount.toLocaleString(),
        limit: ANALYZE_CHAR_LIMIT.toLocaleString(),
      }));
      return;
    }

    // Balance guard. The price is a flat 1 credit, so this is a simple
    // "do you have any credits" check again — no pre-flight quote round-trip
    // is needed to know what a report costs. Admins and unlimited-tier orgs
    // are exempt (they are never reserved against server-side either).
    if (balance !== null && balance <= 0 && !membershipTier?.is_unlimited && !isAdmin) {
      setShowCreditModal(true);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmAnalyze = async () => {
    setShowConfirm(false);
    setLoading(true);
    setError(null);
    setResult(null);
    setReportId(null);
    try {
      const response = await apiFetch('/api/v1/analyze-and-report', {
        method: 'POST',
        body: JSON.stringify(buildAnalyzeRequestBody(text, source, productCategory)),
      });
      if (response.status === 402) {
        // Parse the real 402 body ({error, required_credits,
        // credits_remaining, shortfall}) instead of discarding it, so the
        // credit-exhausted modal shows the true balance rather than assuming
        // zero. Falls back gracefully if the body isn't JSON.
        let detail = null;
        try {
          const body = await response.json();
          detail = body?.detail || null;
        } catch (_e) { /* no parseable body — fall back below */ }
        // TWO DISTINCT 402s, TWO DISTINCT REMEDIES (migration 041). A per-user
        // monthly cap is NOT an empty org pool: the org may have hundreds of
        // credits left. Showing the credit-exhausted modal here would send the
        // user to upgrade a plan that cannot fix their problem, and the credit
        // branch below would overwrite the header balance on the way. Only an
        // org admin raising the cap (or the plan month rolling over) unblocks
        // this, so say that instead.
        if (detail && detail.error === 'user_credit_cap_reached') {
          const cap = detail.user_cap;
          const used = detail.user_period_used;
          setError(
            isKo
              ? `이번 결제 주기의 개인 분석 한도(${cap}건)에 도달했습니다. 현재 ${used}건 사용. 조직 관리자에게 한도 상향을 요청하세요.`
              : `You have reached your monthly limit of ${cap} analyses (${used} used this period). Ask an organization admin to raise your limit.`
          );
          return;
        }
        // T11 — NEITHER BRANCH MAY INVENT A BALANCE (incident O3).
        //
        // This read `setBalance(detail.credits_remaining ?? 0)` and, on the
        // other branch, a flat `setBalance(0)`. The header then printed a
        // confident "0 남음" for a rejection that may have had nothing to do
        // with the pool: a non-credit 402 carries no balance at all.
        //
        // The metered denial DOES always carry one today —
        // `ReserveResult.credits_remaining` is typed `int`, not Optional
        // (subscription_service.py:22), and analyze.py puts it in every
        // insufficient_credits body, as does core/subscription_middleware.py.
        // The null/undefined branch below is therefore belt-and-braces, not a
        // reachable path; it is kept because the alternative to a missing
        // number must never be a fabricated one, and because a future body
        // shape must fail safe. Do not read it as "the field is optional".
        //
        // ⚠ AND THE MODAL WAITS FOR THE RE-READ (review 2026-09-24). This
        // fired `loadBalance()` without awaiting it and opened the dialog in
        // the same tick, so "크레딧이 소진되었습니다" rendered beside the last
        // known POSITIVE pool — a self-contradicting screenshot. Clearing to
        // null first means that if the re-read also fails, the modal shows a
        // dash rather than a stale figure. One extra GET on a path that has
        // already failed, in exchange for never publishing a number the server
        // did not give us.
        if (detail && detail.error === 'insufficient_credits') {
          setCreditShortfall({
            creditsRemaining: detail.credits_remaining,
            shortfall: detail.shortfall,
          });
          if (detail.credits_remaining === undefined || detail.credits_remaining === null) {
            setBalance(null);
            await loadBalance();
          } else {
            setBalance(detail.credits_remaining);
          }
        } else {
          setCreditShortfall(null);
          setBalance(null);
          await loadBalance();
        }
        setShowCreditModal(true);
        return;
      }
      if (!response.ok) {
        // Phase 6: render server INPUT_* envelope verbatim, no auto-retry.
        let bannerMsg = 'Analysis failed';
        try {
          const data = await response.json();
          const raw = data?.detail?.message || data?.detail?.error || data?.detail || `Analysis failed (${response.status})`;
          bannerMsg = escapeForDisplay(typeof raw === 'string' ? raw : JSON.stringify(raw));
        } catch (_e) {
          bannerMsg = `Analysis failed (${response.status})`;
        }
        throw new Error(bannerMsg);
      }
      const data = await response.json();
      // T6 (2026-09-24) — THIS GUARD WAS CORRECT AND IS NOW INVERTED.
      //
      // It used to read: do NOT feed `credits_remaining` into `balance`, because
      // they were DIFFERENT LEDGERS — `balance` came from
      // GET /api/v1/credits/balance -> users.credit_balance (USER ledger), while
      // /analyze-and-report returns organizations.credits_remaining (ORG ledger).
      // Cross-assigning them silently swapped the number in the summary card and
      // the low-credit banner, reverting only on reload.
      //
      // T6 repointed /credits/balance at the ORG pool, so both are now the same
      // ledger and the guard became a bug in the other direction: `credit_balance`
      // is not in this response, so the branch never fired and the header sat at
      // its loaded value while the real pool went down with every analysis.
      if (data.credits_remaining !== undefined) setBalance(data.credits_remaining);

      const analysis = data.analysis || {};

      // The per-unit path debits the org pool by a computed amount, so re-read
      // rather than assuming the single-credit delta above.
      if (analysis.cu_charged !== undefined && analysis.cu_charged !== null) {
        loadBalance();
      }

      // EVERY submission produces a report — always navigate.
      // (Claim-valid-error-sample-data-fix-8-14.md §11.5 T-R.4.) The branch
      // that used to intercept `review_required` and show a notice instead of
      // navigating is gone along with human review; the backend no longer
      // returns that verdict, and a stale `review_required` on an older
      // response must not strand the user on a page with no report.
      navigate('/reports');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const SOURCES = ['Product Label', 'Marketing Copy', 'Website', 'Social Media'];
  const greeting = () => {
    const h = new Date().getHours();
    const nick = user?.nickname?.trim();
    const fullFirst = user?.full_name?.split(' ')[0];
    const name = nick || fullFirst || user?.email?.split('@')[0] || '';
    if (h < 6) return `Good night, ${name}`;
    if (h < 12) return `Good morning, ${name}`;
    if (h < 18) return `Good afternoon, ${name}`;
    return `Good evening, ${name}`;
  };

  // A report costs 1 credit, so "can you afford it" is "do you have any".
  const confirmBlocked = balance === 0 && user?.role !== 'admin';

  const riskColor = (level) => ({ LOW: '#16A34A', MEDIUM: '#D97706', HIGH: '#B91C1C', CRITICAL: '#B91C1C' }[level] || '#6B7280');
  const riskLabel = (level) => ({ LOW: 'Verified Safe', MEDIUM: 'Caution', HIGH: 'High Risk', CRITICAL: 'Critical' }[level] || level || '—');
  const latestReport = recentReports[0] || null;
  const membershipLabel = pendingMembership?.tier_display_name
    || membershipTier?.display_name
    || membershipTier?.name
    || (membershipTier?.is_unlimited ? 'Unlimited' : 'Free Trial');
  const summaryCards = [
    {
      title: 'Workspace',
      value: loading ? 'Running analysis' : 'Ready',
      detail: loading ? 'A report is being generated now.' : 'Paste copy and generate a new report.',
      accent: '#6699FF',
      href: '/settings',
      ariaLabel: 'Open workspace settings',
    },
    {
      // KG이니시스 review (T2.1): a service with a quantity, not a credit
      // balance. Links straight to the usage card; the /credits route below
      // survives only as a redirect for old bookmarks.
      title: isKo ? '남은 분석 리포트' : 'Reports left',
      value: membershipTier?.is_unlimited ? '∞ Unlimited' : balance !== null ? `${balance}` : '—',
      detail: membershipTier?.is_unlimited
        ? (isKo ? '현재 플랜은 분석 리포트 건수 제한이 없습니다.' : 'Your plan has no limit on analysis reports.')
        : (isKo ? '생성 가능한 리포트 수입니다.' : 'Reports you can still run.'),
      accent: balance === 0 && !membershipTier?.is_unlimited ? '#B91C1C' : '#16A34A',
      href: '/settings?tab=subscription',
      ariaLabel: isKo ? '분석 리포트 이용 현황 열기' : 'Open analysis report usage',
    },
    {
      title: 'Membership',
      value: pendingMembership ? 'Pending Activation' : membershipLabel,
      detail: pendingMembership ? `Awaiting activation for ${pendingMembership.tier_display_name}.` : 'Current plan for this workspace.',
      accent: pendingMembership ? '#D97706' : '#a100aa',
      href: '/profile/manage-membership',
      ariaLabel: 'Open membership settings',
    },
    {
      title: 'Recent Reports',
      value: `${recentReports.length}`,
      detail: latestReport
        ? `${riskLabel(latestReport.risk_level)} · ${new Date(latestReport.created_at || latestReport.date).toLocaleDateString()}`
        : 'No completed reports yet.',
      accent: latestReport ? riskColor(latestReport.risk_level) : '#6B7280',
      href: '/reports',
      ariaLabel: 'Open recent reports',
    },
  ];
  const cardSurface = 'var(--bg-secondary)';
  const cardBorder = 'var(--border-color)';
  const cardText = 'var(--text-primary)';
  const cardMuted = 'var(--text-tertiary)';

  return (
    <div className="flex-1 flex flex-col items-center min-h-full" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      {/* Background decorations */}
      <div className="fixed top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'rgba(161,0,170,0.05)', filter: 'blur(120px)', zIndex: 0 }} />
      <div className="fixed bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: 'rgba(0,51,153,0.07)', filter: 'blur(100px)', zIndex: 0 }} />

      <div className="w-full max-w-4xl px-6 md:px-8 py-16 md:py-20 flex flex-col items-center relative z-10">
        {/* P1-B: Low-credit banner (≤1 credit, paid plans excluded) */}
        <div className="w-full max-w-[720px] mb-4">
          <LowCreditBanner
            balance={balance}
            isUnlimited={!!membershipTier?.is_unlimited}
            isAdmin={user?.role === 'admin'}
            /* "이번 달" only for a tier the server says grants monthly; a trial
               org's reports are one-time. `=== true`: unknown is not "yes". */
            monthly={orgCredits?.tier_grants_monthly === true}
          />
        </div>
        {/* Welcome banner */}
        {welcomeOrg && (
          <div className="w-full max-w-[720px] mb-6 flex items-center justify-between gap-3 px-5 py-3 rounded-xl" style={{ backgroundColor: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.2)' }}>
            <span className="text-sm" style={{ color: '#16A34A' }}>🎉 Welcome! You are the Org-Admin of <strong>{welcomeOrg}</strong>. <Link to="/admin/users" style={{ color: '#6699FF', textDecoration: 'underline' }}>Add team members →</Link></span>
            <button onClick={() => setWelcomeOrg(null)} className="text-lg leading-none" style={{ color: '#16A34A', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
        )}

        {/* Pending membership banner */}
        {pendingMembership && !dismissPending && (
          <div className="w-full max-w-[720px] mb-6 flex items-start justify-between gap-3 px-5 py-3 rounded-xl" style={{ backgroundColor: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.2)' }}>
            {/* Localised, and the trial quoted from the constant (T2.1). It
                said "You have Free Trial credits (5)" in English only, which
                also claimed all 5 were still there. Modelled on the checkout's
                wire-transfer notice (ManageMembershipPage, plan T1.5).
                Copy status: pending_ceo (D9). */}
            <span className="text-sm" style={{ color: '#D97706' }}>
              {isKo ? (
                <>⏳ <strong>{pendingMembership.tier_display_name}</strong> 멤버십 활성화를 기다리고 있습니다. 그동안 무료 체험(가입 시 제공된 분석 리포트 {TRIAL_SIGNUP_CREDITS}건)으로 VeriPass를 이용하실 수 있습니다.</>
              ) : (
                <>⏳ Your <strong>{pendingMembership.tier_display_name}</strong> membership is pending activation. Until then, you can use VeriPass on the Free Trial ({TRIAL_SIGNUP_CREDITS} analysis reports provided at signup).</>
              )}{' '}
              <Link to="/settings?tab=subscription" style={{ color: '#6699FF' }}>{isKo ? '상태 보기 →' : 'View status →'}</Link>
            </span>
            <button onClick={() => setDismissPending(true)} className="text-lg leading-none mt-0.5" style={{ color: '#D97706', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
        )}

        {/* Greeting */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-5xl font-headline font-extrabold tracking-tight mb-4" style={{ color: cardText }}>
            {greeting()} 👋
          </h1>
          <p className="text-base md:text-lg max-w-lg mx-auto leading-relaxed" style={{ color: cardMuted }}>
            마케팅 문구의 규정 준수를 검증하세요. 아래에 클레임을 입력하거나 붙여넣으세요.
          </p>
        </div>

        {/* Chat-style input container */}
        <div
          className="w-full max-w-[720px] rounded-2xl shadow-2xl transition-all duration-300 focus-within:border-[#a100aa]"
          style={{ backgroundColor: 'var(--bg-secondary)', border: '1.5px solid var(--border-color)' }}
        >
          {/* Textarea */}
          <div className="p-4 pb-2">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              /* NOT the character limit. maxLength here would make going over
                 impossible, which sounds like a feature and isn't: a paste
                 would be silently truncated and the user would submit — and be
                 charged for — text they never saw us drop. The limit is
                 enforced by the counter + the Analyze click + the server; this
                 is only the DoS backstop (10x). */
              maxLength={ANALYZE_HARD_MAX}
              placeholder="검증할 마케팅 문구를 입력하세요 (최대 300자). Enter one marketing claim (300 characters max)."
              className="analyze-textarea bg-transparent border-none focus:outline-none focus:ring-0 w-full min-h-[100px] max-h-[280px] overflow-y-auto resize-none text-[15px] p-0"
              style={{ color: 'var(--text-primary)' }}
            />
            {/* Character counter — ALWAYS visible, from the very first render.
                The whole point of a hard character limit is that the user
                meets it while typing, not at submit time, so this must never
                be threshold-gated (it used to appear only past 80,000 chars,
                i.e. only once the situation was already hopeless). */}
            <div className="text-[11px] mt-1" style={{ color: overCharLimit ? '#B91C1C' : 'var(--text-tertiary)' }}>
              {charCount.toLocaleString()} / {ANALYZE_CHAR_LIMIT.toLocaleString()}
            </div>
            {/* Over-limit alert, directly BELOW the count. role="alert" so it
                is announced rather than only seen. */}
            {overCharLimit && (
              <div className="text-[11px] mt-1" role="alert" style={{ color: '#B91C1C' }}>
                {t('analyze.charLimit.exceeded', {
                  over: (charCount - ANALYZE_CHAR_LIMIT).toLocaleString(),
                  limit: ANALYZE_CHAR_LIMIT.toLocaleString(),
                })}
              </div>
            )}
            {/* Anything else validateText rejects (NUL bytes, ...) — these DO
                hard-disable the button, so they get the raw validator message. */}
            {text.length > 0 && hardInvalid && (
              <div className="text-[11px] mt-1" style={{ color: '#B91C1C' }}>
                {claimResult.message}
              </div>
            )}
          </div>

          {/* Source Chips */}
          <div className="px-4 pb-4">
            <p className="text-[10px] font-extrabold uppercase mb-2 tracking-wider" style={{ color: 'var(--text-tertiary)' }}>출처 / Source</p>
            <div className="flex flex-wrap gap-2">
              {SOURCES.map(s => (
                <button
                  key={s}
                  onClick={() => setSource(s)}
                  className="px-4 py-1.5 rounded-full text-[13px] transition-all font-medium"
                  style={source === s
                    ? { backgroundColor: 'rgba(161,0,170,0.15)', border: '1.5px solid #a100aa', color: 'var(--text-primary)', fontWeight: 700 }
                    : { border: '1px solid var(--border-color)', color: 'var(--text-secondary)', background: 'transparent' }
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Product category pre-check */}
          <fieldset className="product-category-group">
            <legend>{t('analyze.productCategoryLegend')}</legend>
            <div className="product-category-options">
              <label className={`product-category-option ${productCategory === 'cosmetic' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="productCategory"
                  value="cosmetic"
                  checked={productCategory === 'cosmetic'}
                  onChange={(e) => setProductCategory(e.target.value)}
                />
                <span>{t('analyze.productCategoryCosmetic')}</span>
              </label>
              <label
                className={`product-category-option ${productCategory === 'otc' ? 'selected' : ''}`}
                title={t('analyze.productCategoryOtcHint')}
              >
                <input
                  type="radio"
                  name="productCategory"
                  value="otc"
                  checked={productCategory === 'otc'}
                  onChange={(e) => setProductCategory(e.target.value)}
                />
                <span>{t('analyze.productCategoryOtc')}</span>
              </label>
              <label className={`product-category-option ${productCategory === 'unknown' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="productCategory"
                  value="unknown"
                  checked={productCategory === 'unknown'}
                  onChange={(e) => setProductCategory(e.target.value)}
                />
                <span>{t('analyze.productCategoryUnknown')}</span>
              </label>
            </div>
            {productCategory === 'otc' && (
              <p className="product-category-hint">{t('analyze.productCategoryOtcHint')}</p>
            )}
          </fieldset>

          {/* Bottom Bar */}
          <div className="flex justify-between items-center p-3" style={{ borderTop: '1px solid var(--border-color)' }}>
            {/* This used to promise "제품 상세 페이지 전체를 붙여넣어도 됩니다"
                (paste the whole product detail page). At 300 characters the box
                holds ONE marketing or packaging claim — that promise is off by
                two orders of magnitude, and inviting a paste we then reject is
                worse than not inviting it. Whole-page ingestion is a separate
                future flow (upload/URL), not this box. */}
            {/* It said "리포트 1건 = 크레딧 1개", in Korean even in English mode.
                The limit is the same constant the counter above uses. */}
            <p className="text-[11px] font-medium ml-1" style={{ color: 'var(--text-tertiary)' }}>
              {isKo
                ? `💡 문구 1건(최대 ${ANALYZE_CHAR_LIMIT.toLocaleString()}자) = 분석 리포트 1건`
                : `💡 1 claim (up to ${ANALYZE_CHAR_LIMIT.toLocaleString()} characters) = 1 analysis report`}
            </p>
            <button
              onClick={handleAnalyzeClick}
              /* NOT disabled on overCharLimit — that is deliberate. The click
                 is what explains the limit; disabling the button would leave a
                 user with an over-long paste staring at a dead control and no
                 stated reason. */
              disabled={loading || !text.trim() || hardInvalid}
              className="flex items-center gap-2 font-extrabold text-[14px] text-white px-6 py-2 rounded-full shadow-lg transition-all"
              style={{ backgroundColor: '#a100aa', opacity: (loading || !text.trim() || hardInvalid) ? 0.3 : 1, cursor: (loading || !text.trim() || hardInvalid) ? 'default' : 'pointer' }}
            >
              <span>{loading ? 'Analyzing...' : 'Analyze'}</span>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span>
            </button>
          </div>
        </div>

        {/* T11 — the org pool, through the SAME panel the Settings 구독 card and
            the User Management header now use. The line this replaces printed
            `balance` on its own: no allowance, no usage, no renewal date, and
            its own private colour rule. A customer comparing this screen with
            Settings could not tell whether they were reading the same pool. */}
        {creditsLoaded && (
          <div className="w-full max-w-[720px] mt-4 flex justify-center">
            <OrgCreditPanel
              isKo={isKo}
              /* GET /credits/balance reads organizations.credits_remaining since
                 T6, so this IS the org pool — the same number the panel shows in
                 Settings, and the one an analysis spends. */
              remaining={balance}
              granted={orgCredits?.credit_limit ?? membershipTier?.credit_limit ?? null}
              usedThisPeriod={orgCredits?.credits_used_this_period ?? null}
              planStartDate={planStartDate}
              /* Clamps the anniversary walk to the term — the final month of a
                 fixed-term plan grants nothing, so it must show a dash. */
              planEndDate={planEndDate}
              /* Whether a grant is coming at all, decided SERVER-side from
                 product_catalogue (monthly_credits_for_tier). An active
                 membership proves a term, not a grant: trial, enterprise,
                 studio and the fgi_* tiers are all assignable and all grant
                 nothing, and the panel used to derive an anniversary from the
                 term for every one of them. Undefined/null means "could not
                 establish", and the panel renders a dash rather than guessing
                 either way. */
              tierGrantsMonthly={orgCredits?.tier_grants_monthly ?? null}
              /* `is_unlimited` is computed server-side from an ACTIVE membership
                 row, so unlike a bare `credit_limit == null` it cannot confuse
                 "unlimited tier" with "no membership at all". */
              unlimited={!!membershipTier?.is_unlimited}
              unavailableReason={balance === null && !membershipTier?.is_unlimited
                ? (isKo
                  ? '남은 분석 리포트 건수를 불러오지 못했습니다. 새로 고침 후 다시 시도해 주세요.'
                  : 'Could not load your remaining analysis reports. Reload and try again.')
                : null}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="w-full max-w-[720px] mt-4 px-5 py-3 rounded-xl text-sm" style={{ backgroundColor: 'rgba(185,28,28,0.1)', border: '1px solid rgba(185,28,28,0.3)', color: '#F87171' }}>
            {error}
          </div>
        )}

        {/* The "판정 미완료 — 검토 필요" balloon that used to live here is GONE
            (Claim-valid-error-sample-data-fix-8-14.md §11.5 T-R.4).
            Human review is no longer a product outcome: every submission now
            returns one of four categories — COSMETIC / OTC_DRUG / DRUG /
            NON_COSMETIC — and navigates to a report. There is no longer any
            input that produces a notice instead of an analysis. */}

        <ExampleClaimsSection presets={PRESET_EXAMPLES} onSelectExample={setExampleModal} />

        <DashboardSummaryCards
          cards={summaryCards}
          cardSurface={cardSurface}
          cardBorder={cardBorder}
          cardMuted={cardMuted}
        />

        {/* Recent Verifications */}
        {recentReports.length > 0 && (
          <div className="w-full max-w-[720px] mt-16">
            <div className="flex justify-between items-end mb-6">
              <h2 className="font-headline font-extrabold text-xl tracking-tight" style={{ color: cardText }}>Recent Verifications</h2>
              <button onClick={() => navigate('/records')} className="text-sm font-bold" style={{ color: '#a100aa' }}>View History</button>
            </div>
            <div className="space-y-3">
              {recentReports.map((r, i) => (
                <button
                  key={r.id || i}
                  onClick={() => navigate('/reports')}
                  className="w-full text-left p-5 rounded-xl flex items-center justify-between group transition-all"
                  style={{ backgroundColor: cardSurface, border: `1px solid ${cardBorder}` }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = cardSurface}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(161,0,170,0.1)' }}>
                      <span className="material-symbols-outlined" style={{ color: '#a100aa' }}>description</span>
                    </div>
                    <div>
                      {/* §8 P5 T5.4 — firstLine() truncates to a single ~96-char
                          line BEFORE it ever reaches the DOM, so a page-sized
                          paste doesn't sit in memory just to be CSS-ellipsized. */}
                      <h3 className="font-semibold text-sm truncate max-w-[300px]" style={{ color: cardText }}>{firstLine(r.input_text || r.text || '—')}</h3>
                      <p className="text-xs mt-0.5" style={{ color: cardMuted }}>{r.source || '—'} · {new Date(r.created_at || r.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 rounded-md text-[10px] font-black tracking-widest uppercase" style={{ backgroundColor: `${riskColor(r.risk_level)}15`, color: riskColor(r.risk_level), border: `1px solid ${riskColor(r.risk_level)}20` }}>
                      {riskLabel(r.risk_level)}
                    </span>
                    <span className="material-symbols-outlined" style={{ color: cardMuted }}>chevron_right</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.65)' }} onClick={() => setShowConfirm(false)}>
          <div className="mx-4 w-full max-w-[440px] p-8 rounded-2xl shadow-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} onClick={e => e.stopPropagation()}>
            <h2 className="font-headline font-bold text-lg mb-4" style={{ color: 'var(--text-primary)' }}>Confirm Analysis</h2>
            <div className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
              <span style={{ color: 'var(--text-tertiary)' }}>Input:</span>{' '}
              <span style={{ color: 'var(--text-primary)' }}>{text.length > 60 ? text.substring(0, 60) + '...' : text}</span>
            </div>
            {/* Flat price, unconditionally. There is no quote to fetch and no
                CU breakdown to justify: whatever fits in the box uses one
                analysis report.

                The "Org-Admins: credits are not consumed." line that sat under
                this is GONE (F-12): analyze.py charges every org that is not
                unlimited, admins included. The client-side admin exemptions
                that rest on the same premise (handleAnalyzeClick,
                confirmBlocked, the "N left" suffix below) are a behaviour
                question, flagged separately rather than changed here. */}
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {isKo ? (
                <>분석 리포트 <strong style={{ color: 'var(--text-primary)' }}>1건</strong>이 사용됩니다{user?.role !== 'admin' && balance !== null && balance > 0 ? ` (사용 후 ${(balance - 1).toLocaleString()}건 남음)` : ''}.</>
              ) : (
                <>This uses <strong style={{ color: 'var(--text-primary)' }}>1 analysis report</strong>{user?.role !== 'admin' && balance !== null && balance > 0 ? ` (${(balance - 1).toLocaleString()} left afterwards)` : ''}.</>
              )}
            </p>
            {confirmBlocked && (
              <p className="text-xs mb-3" style={{ color: '#F87171' }}>
                {isKo ? '남은 분석 리포트가 없습니다. 조직 관리자에게 문의해 주세요.' : 'No analysis reports left. Please contact your Org-Admin.'}
              </p>
            )}
            <div className="flex gap-3 mt-6">
              <button onClick={handleConfirmAnalyze} disabled={confirmBlocked} className="flex-1 py-3 rounded-xl font-bold text-white text-sm" style={{ backgroundColor: '#a100aa', opacity: confirmBlocked ? 0.4 : 1 }}>Confirm</button>
              <button onClick={() => setShowConfirm(false)} className="flex-1 py-3 rounded-xl font-bold text-sm" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Example Modal */}
      {exampleModal && PRESET_EXAMPLES[exampleModal] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.65)' }} onClick={() => setExampleModal(null)}>
          <div className="mx-4 w-full max-w-[500px] p-8 rounded-2xl shadow-2xl max-h-[80vh] overflow-y-auto" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-headline font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{t(PRESET_EXAMPLES[exampleModal].titleKey)}</h2>
              <button onClick={() => setExampleModal(null)} style={{ color: 'var(--text-tertiary)' }}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="space-y-2">
              {PRESET_EXAMPLES[exampleModal].claims.map((claim, i) => (
                <button
                  key={i}
                  onClick={() => { setText(claim); setSource('Product Label'); setError(null); setExampleModal(null); }}
                  className="w-full text-left text-sm px-4 py-3 rounded-lg transition-all"
                  style={{ color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#a100aa'; e.currentTarget.style.backgroundColor = 'rgba(161,0,170,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.backgroundColor = 'var(--bg-primary)'; }}
                >
                  {claim}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* P1-B: Credit-exhausted modal. creditShortfall carries the real
          numbers from a 402 body; null when none applies, in which case the
          modal falls back to its balance-only copy.

          ⚠ `balance ?? 0` USED TO BE HERE, AND IT WAS THE O3 DEFECT ITSELF
          (review 2026-09-24). The modal renders this figure (today as
          "남은 분석 리포트 {n}건"; then "잔여 크레딧 {n}개") in bold red, so a
          failed GET /credits/balance printed a confident zero — on the same
          screen, at the same moment, as the panel above it correctly saying
          the balance could not be read. The modal renders whatever it is
          handed, so it is handed the same dash the panel uses. `== null` on
          purpose: the prop's own `= 0` default would otherwise catch
          `undefined` and re-fabricate the zero.

          `monthly` (T2.3): the title says "이번 달" only for a tier the
          server says grants monthly. */}
      <CreditExhaustedModal
        open={showCreditModal}
        onClose={() => { setShowCreditModal(false); setCreditShortfall(null); }}
        tierName={membershipTier?.display_name || membershipTier?.name || 'Free'}
        balance={balance == null ? DASH : balance}
        shortfall={creditShortfall?.shortfall}
        monthly={orgCredits?.tier_grants_monthly === true}
      />
    </div>
  );
}

// Root redirect component - shows landing page if not authenticated, redirects to /app if authenticated
function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();

  // While auth state is resolving, show landing page (it's public — no flash issue)
  if (loading) return <LandingPage />;

  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  return <LandingPage />;
}

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    // Set data-theme attribute for CSS variables
    document.documentElement.setAttribute('data-theme', theme);
    
    // Add/remove 'dark' class for Tailwind CSS dark mode
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <AuthProvider>
      <Router>
        <AppLayout theme={theme} setTheme={setTheme}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/verified" element={<VerifiedPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/account/cancel-deletion" element={<CancelDeletionPage />} />
            <Route path="/reports/:id" element={<ReportDetailPage theme={theme} setTheme={setTheme} />} />
            <Route path="/signup/pending" element={<SignupPendingPage />} />
            
            {/* Redirects for old routes */}
            <Route path="/ethics" element={<Navigate to="/about#ethics" replace />} />
            <Route path="/privacy" element={<Navigate to="/about#privacy" replace />} />
            <Route path="/faq" element={<Navigate to="/settings?tab=support" replace />} />
            <Route path="/feedback" element={<Navigate to="/settings?tab=support" replace />} />
            <Route path="/credits" element={<Navigate to="/settings?tab=subscription" replace />} />
            <Route path="/content" element={<Navigate to="/app" replace />} />
            <Route path="/test" element={<Navigate to="/app" replace />} />
            
            {/* Protected Routes */}
            <Route path="/app" element={<ProtectedRoute permission="access:analysis"><AnalysisPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/profile/manage-membership" element={<ProtectedRoute><ManageMembershipPage /></ProtectedRoute>} />
            <Route path="/records" element={<ProtectedRoute permission="access:my_reports"><RecordsPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute permission="access:test_runner"><AdminTestRunner /></ProtectedRoute>} />
            {/* Retired standalone page — Warning Letters is now a tab in Data Explorer. Redirect preserves old bookmarks. */}
            <Route path="/admin/warning-letters" element={<Navigate to="/admin/data-explorer?tab=warning-letters" replace />} />
            <Route path="/admin/data-explorer" element={<ProtectedRoute permission="access:data_explorer"><DataExplorer /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute permission="access:user_management"><UserManagement /></ProtectedRoute>} />
            <Route path="/admin/site-users" element={<ProtectedRoute requireSiteAdmin><SiteAdminUserManager /></ProtectedRoute>} />
            {/* PortOne Refund Manager. requireSiteAdmin, matching the router-level
                gate on the backend - a permission would be self-grantable. */}
            <Route path="/admin/refunds" element={<ProtectedRoute requireSiteAdmin><RefundManager /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute permission="access:payment_management"><ManualPaymentManager /></ProtectedRoute>} />
            <Route path="/admin/gold" element={<ProtectedRoute permission="access:gold_dataset"><GoldDatasetManager /></ProtectedRoute>} />
            <Route path="/admin/roles" element={<ProtectedRoute permission="access:role_management"><RoleManagement /></ProtectedRoute>} />
            <Route path="/admin/audit" element={<ProtectedRoute permission="access:audit_log"><AuditLog /></ProtectedRoute>} />
            <Route path="/admin/sessions" element={<ProtectedRoute permission="admin:sessions"><AdminSessionManager /></ProtectedRoute>} />
            <Route path="/admin/pipeline" element={<ProtectedRoute permission="access:test_runner"><PipelineHealthDashboard /></ProtectedRoute>} />
            <Route path="/admin/email" element={<ProtectedRoute requireSiteAdmin><EmailServiceManager /></ProtectedRoute>} />
            <Route path="/admin/marketing-ai" element={<ProtectedRoute requireSiteAdmin><MarketingAIPage /></ProtectedRoute>} />
            <Route path="/admin/promotions" element={<ProtectedRoute requireSiteAdmin><PromotionManager /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute permission="access:my_reports"><UserReportPage /></ProtectedRoute>} />
            <Route path="/admin/profiles" element={<ProtectedRoute requireSiteAdmin><AdminProfileManager /></ProtectedRoute>} />
          </Routes>
        </AppLayout>
      </Router>
    </AuthProvider>
  );
}

export default App;
